import { CookiesLabels } from "../../../assets/labels/cookies.labels";
import appConfig from "../../appConfig";
import Cookies from 'universal-cookie';

declare global {
    interface Window {
        Android?: {
            getAppCookies?: () => string;
            setAppCookies?: (cookies: string) => void;
        };
        getCookiesFromApp?: typeof Helper.getCookiesFromApp;
        setCookiesToApp?: typeof Helper.setCookiesToApp;
      __appCookies?: string;
    }
}

export class Helper {
  static cookies = new Cookies();

  static formatDistance(distanceInMeters: number | undefined): string | undefined {
    if (distanceInMeters && typeof distanceInMeters === "number") {
      if (distanceInMeters < 1000) {
        return ` - ${distanceInMeters?.toFixed(1)} m`;
      } else {
        const distanceInKilometers = distanceInMeters / 1000;
        return ` - ${distanceInKilometers.toFixed(1)} km`;
      }
    } else {
      return undefined;
    }
  }

  static passwordRegex = "^(?=.*[a-z])(?=.*[A-Z])(?=.*[0-9])(?=.*[^A-Za-z0-9])[a-zA-Z0-9\\S]{8,}";
  static testPasswordRegex = (value: string) => {
    const regex = new RegExp(this.passwordRegex);
    return regex.test(value);
  };

  static emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  static testEmailRegex = (value: string) => {
    return this.emailRegex.test(value);
  };

  static isAuthPage = (currentPath: string) => {
    const authSubpaths = ["login", "registo", "recuperar", "email", "password"];
    return authSubpaths.some(path => currentPath.includes(path));
  }

  static isLoginPage = (currentPath: string) => {
    const authSubpaths = ["login"];
    return authSubpaths.some(path => currentPath.includes(path));
  }

  static isRegisterPage = (currentPath: string) => {
    const authSubpaths = ["registo"];
    return authSubpaths.some(path => currentPath.includes(path));
  }

  static isConfirmEmailPage = (currentPath: string) => {
    const authSubpaths = ["confirm-email"];
    return authSubpaths.some(path => currentPath.includes(path));
  }
  
  static isEmbeddedConst: string | null | undefined = undefined;
  static isEmbedded = () => {
    if (Helper.isEmbeddedConst) return Helper.isEmbeddedConst;

    const searchParams = new URLSearchParams(window.location.search);
    Helper.isEmbeddedConst = searchParams.get("embedded");
    return Helper.isEmbeddedConst==='true';
  }

  static getCookiesMessage = () => CookiesLabels.cookiesMessage.replace("{0}", "Keymaster");
  
  static cookieNames = [
    'acceptedAnalyticsCookies',
    'acceptedNecessaryCookies',
    'sessionToken',
    'prevCompanyDomain',
    'prevCompanyUrl',
    'videoTutorial',
    'readLocationRequest',
  ];

  static setCookiesToApp = () => {
    return new Promise<void>((resolve: () => void) => {
      const allCookies = document.cookie.split(';').reduce((acc, cookie) => {
        const [name, ...rest] = cookie.split('=');
        if (name && rest.length) {
          acc[name.trim()] = rest.join('=').trim();
        }
        return acc;
      }, {} as Record<string, string>);
      const selectedCookies = Helper.cookieNames
        .map(name => allCookies[name] !== undefined ? `${name}=${allCookies[name]}` : null)
        .filter(Boolean)
        .join('; ');
      if (HelperWebView.isAndroidWebview() && (window as any).Android && typeof (window as any).Android.setAppCookies === 'function') {
        try {
          (window as any).Android.setAppCookies(selectedCookies || '');
        } catch (err) {
          console.error('Failed to serialize cookies to Android app:', err);
        }
      }
      //console.log("Checking if iOS and if setAppCookies exists");
      if (HelperWebView.isIOSWebview() && (window as any).webkit?.messageHandlers?.setAppCookies) {
        //console.log("iOS WebView detected and setAppCookies exists");
        try {
          //console.log("Setting cookies in iOS WebView:", selectedCookies);
          (window as any).webkit.messageHandlers.setAppCookies.postMessage({ cookies: selectedCookies || '' });
        } catch (error) {
          //console.error('Failed to set cookies in iOS WebView:', error);
        }
      } else {
        //console.log("Not iOS or setAppCookies does not exist");
      }
      resolve();
    });
  };

  static setCookie = (name: string, value: string) => {
    Helper.cookies.set(name, value, HelperConfig.cookiesConfig);
    if (Helper.cookieNames.includes(name)) {
      Helper.setCookiesToApp();
    }
  };

  static removeCookie = (name: string) => {
    Helper.cookies.remove(name);
    if (Helper.cookieNames.includes(name)) {
      Helper.setCookiesToApp();
    }
  };

  /**
 * Retrieves cookies from the Android app storage and sets them in the browser.
 */
  static getCookiesFromApp = (): Promise<void> => {
    return new Promise<void>((resolve) => {
      //Check if there is no cookie sessionToken or if the cookie empty
      if (!Helper.cookies.get('sessionToken') || Helper.cookies.get('sessionToken') === '') {
        if (HelperWebView.isAndroidWebview() && window.Android && typeof window.Android.getAppCookies === 'function') {
          try {
            const cookiesString = window.Android.getAppCookies();
            if (cookiesString) {
              // Parse cookies into an object first
              const cookieObj: Record<string, string> = {};
              cookiesString.split(';').forEach(cookie => {
                const [name, ...rest] = cookie.split('=');
                if (name && rest.length) {
                  cookieObj[name.trim()] = rest.join('=').trim();
                }
              });
              // Only apply cookies if sessionToken is present
              if (cookieObj.sessionToken) {
                Object.entries(cookieObj).forEach(([cookieName, cookieValue]) => {
                  Helper.cookies.set(cookieName, cookieValue, HelperConfig.cookiesConfig);
                });
              }
            }
          } catch (err) {
            console.error('Failed to get cookies:', err);
          }
        }
        if (HelperWebView.isIOSWebview()) {
          try {
            const cookiesString = window.__appCookies;
            if (cookiesString) {
              // Parse cookies into an object first
              const cookieObj: Record<string, string> = {};
              cookiesString.split(';').forEach(cookie => {
                const [name, ...rest] = cookie.split('=');
                if (name && rest.length) {
                  cookieObj[name.trim()] = rest.join('=').trim();
                }
              });
              // Only apply cookies if sessionToken is present
              if (cookieObj.sessionToken) {
                Object.entries(cookieObj).forEach(([cookieName, cookieValue]) => {
                  Helper.cookies.set(cookieName, cookieValue, HelperConfig.cookiesConfig);
                });
              }
            }
          } catch (err) {
            console.error('Failed to get cookies:', err);
          }
        }
      }
      resolve();
    });
  };
};

export class HelperWebView {
  // Helper user agent
  static isAndroid = () => {
    // Check for Android user agent
    const userAgent = window.navigator.userAgent.toLowerCase();
    return userAgent.includes("android");
  };
  static isAndroidBrowser = () => {
    const userAgent = window.navigator.userAgent.toLowerCase();

    // Check for specific browser properties
    const isBrowser = /android/.test(userAgent) && window.navigator.vendor.includes("Google");

    return this.isAndroid() && isBrowser;
  };
  static isAndroidWebview = () => {
    // Check for Android user agent
    const userAgent = window.navigator.userAgent;

    // Check for specific WebView properties
    return (
      userAgent.includes("AndroidAppCustomerMobile") ||
      userAgent.includes("AndroidAppCustomerTablet") ||
      userAgent.includes("wv")
    );
  };
  // iOS
  static isIOS = () => {
    // Check for iOS user agent
    const userAgent = window.navigator.userAgent.toLowerCase();
    return /iphone|ipad|ipod/.test(userAgent);
  };
  static isIOSWebview = () => {
    // Check for specific WebView properties
    const userAgent = window.navigator.userAgent;

    return userAgent.includes("iOSAppCustomerMobile") || userAgent.includes("iOSAppCustomerTablet");
  };

  static nativeAppToken = () => {
    let appToken: string | undefined = undefined;
    if (HelperWebView.isAndroidWebview()) {
      try {
        if ((window as any).Android && typeof (window as any).Android.getAuthToken === 'function') {
          appToken = (window as any).Android.getAuthToken();
        }
      } catch (error) {
        console.error('Failed to get Android app token:', error);
      }
    } else if (HelperWebView.isIOSWebview()) {
      if ((window as any).__authToken) {
        appToken = (window as any).__authToken;
      }
    }
    return appToken;
  };
}

export class HelperConfig {
  static isQAenvironment = () => window.location.hostname?.includes(".qa.keymaster.pt") || window.location.hostname.includes("localhost");
  static welcomePageLink = () =>
    this.isQAenvironment()
    ? "https://asuaempresa.qa.keymaster.pt/"
    : "https://asuaempresa.keymaster.pt/";
  static cookiesConfig = { domain: process.env.NODE_ENV === 'development' ? 'localhost' : 'keymaster.pt', path: '/', maxAge: 31556926};
  static cookiesLiteConfig = { domain: process.env.NODE_ENV === 'development' ? 'localhost' : 'keymaster.pt', path: '/'};
}

export class HelperRedirect {
  static showCompaniesClient = (domain: string = 'checkpoint', iagree: boolean = false) => {
    const url = new URL(`https://${domain}.${appConfig.DOMAIN}/client/services?showCompanies=true`).href;
    window.open(url, '_self');
  };

  static goToAppClient = (domain: string = 'checkpoint', openAppointment: string = '', iagree: boolean = false, redirectTo: string = '') => {
    let url;
    url = redirectTo? new URL(`${redirectTo}`) : new URL(`https://${domain}.${appConfig.DOMAIN}/client/services`);
    if (openAppointment) {
      url.searchParams.append('openAppointment', 'true');
    }
    if (iagree) {
      url.searchParams.append('iagree', 'true');
    }
    window.open(url.href, '_self');
  }
  
};

if (typeof window !== 'undefined') {
    window.getCookiesFromApp = Helper.getCookiesFromApp;
    window.setCookiesToApp = Helper.setCookiesToApp;
}
